import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import LegacyApprovalViewContainer from './ApprovalViewContainer';
import AskDialog from '../../components/common/dialog/AskDialog';
import ApprovalV2ActionDialog from '../../components/common/approval/ApprovalV2ActionDialog';
import ApprovalV2HistoryPanel from '../../components/common/approval/ApprovalV2HistoryPanel';
import * as approvalV2Api from '../../lib/api/approvalV2';
import * as hrAttendApi from '../../lib/api/hrAttend';
import * as projectApi from '../../lib/api/project';

const DOC_TYPE_LABELS = {
  HR_LEAVE: '휴가 신청',
  ORDER_BOOK: '수주품의서',
};

const DOC_STATUS_LABELS = {
  IN_PROGRESS: '진행중',
  APPROVED: '승인',
  REJECTED: '반려',
  RECALLED: '회수',
  CANCELED: '취소',
};

const DOC_STATUS_COLORS = {
  IN_PROGRESS: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  RECALLED: 'default',
  CANCELED: 'default',
};

const ApprovalHybridViewContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));

  const [loading, setLoading] = useState(false);
  const [legacyMode, setLegacyMode] = useState(false);
  const [docDetail, setDocDetail] = useState(null);
  const [leaveDetail, setLeaveDetail] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, mode: null });
  const [recallVisible, setRecallVisible] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setLegacyMode(false);
      setDocDetail(null);
      setLeaveDetail(null);
      setOrderDetail(null);

      try {
        const res = await approvalV2Api.getApprovalV2Document(id);
        const info = res.data.info;
        if (!info) {
          setLegacyMode(true);
          return;
        }

        const nextDetail = {
          info,
          steps: res.data.steps || [],
          actions: res.data.actions || [],
        };
        setDocDetail(nextDetail);

        if (info.docType === 'HR_LEAVE') {
          const leaveRes = await hrAttendApi.getLeaveRequest(info.refDocId);
          setLeaveDetail({
            info: leaveRes.data.info,
            steps: nextDetail.steps,
            actions: nextDetail.actions,
          });
        }

        if (info.docType === 'ORDER_BOOK') {
          const orderRes = await projectApi.getOrderInfoAll(info.refDocId);
          setOrderDetail(orderRes.data);
        }
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.message || '결재 상세 조회에 실패했습니다.', { variant: 'error' });
        setLegacyMode(true);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [enqueueSnackbar, id, tick]);

  const handleApproveReject = async (comment) => {
    if (!docDetail?.info) {
      return;
    }

    try {
      if (docDetail.info.docType === 'HR_LEAVE') {
        if (actionDialog.mode === 'APPROVE') {
          await hrAttendApi.approveLeaveRequest(docDetail.info.refDocId, { rmk: comment });
        } else {
          await hrAttendApi.rejectLeaveRequest(docDetail.info.refDocId, { rmk: comment });
        }
      } else if (docDetail.info.docType === 'ORDER_BOOK') {
        if (actionDialog.mode === 'APPROVE') {
          await projectApi.approveReceiveOrderApprovalV2(docDetail.info.refDocId, { comment });
        } else {
          await projectApi.rejectReceiveOrderApprovalV2(docDetail.info.refDocId, { comment });
        }
      }

      enqueueSnackbar(actionDialog.mode === 'APPROVE' ? '결재를 승인했습니다.' : '결재를 반려했습니다.', {
        variant: 'success',
      });
      setActionDialog({ open: false, mode: null });
      setTick((value) => value + 1);
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || '결재 처리에 실패했습니다.', { variant: 'error' });
    }
  };

  const handleRecall = async () => {
    if (!docDetail?.info) {
      return;
    }

    try {
      if (docDetail.info.docType === 'HR_LEAVE') {
        await hrAttendApi.recallLeaveRequest(docDetail.info.refDocId);
      } else if (docDetail.info.docType === 'ORDER_BOOK') {
        await projectApi.recallReceiveOrderApprovalV2(docDetail.info.refDocId);
      }

      enqueueSnackbar('문서를 회수했습니다.', { variant: 'success' });
      setRecallVisible(false);
      setTick((value) => value + 1);
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || '회수에 실패했습니다.', { variant: 'error' });
    }
  };

  const canApprove = useMemo(() => {
    return docDetail?.info
      && docDetail.info.docStatus === 'IN_PROGRESS'
      && docDetail.info.currentActorUsrUuid === user?.usrUuid;
  }, [docDetail, user]);

  const canRecall = useMemo(() => {
    return docDetail?.info
      && docDetail.info.docStatus === 'IN_PROGRESS'
      && docDetail.info.submittedBy === user?.usrUuid;
  }, [docDetail, user]);

  const backTarget = useMemo(() => {
    const from = location.state?.from;
    if (from === 'submitted') {
      return '/approvalmng/submitted';
    }
    return '/approvalmng/inbox';
  }, [location.state]);

  if (legacyMode) {
    return <LegacyApprovalViewContainer />;
  }

  const info = docDetail?.info;

  return (
    <>
      <BreadcrumbsContainer title="결재관리/결재상세" />

      {loading && !info ? (
        <Typography variant="body2" color="text.secondary">결재 문서를 불러오는 중입니다.</Typography>
      ) : null}

      {info ? (
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h5">{info.docTitle}</Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={DOC_TYPE_LABELS[info.docType] || info.docType}
                  />
                  <Chip
                    size="small"
                    label={DOC_STATUS_LABELS[info.docStatus] || info.docStatus}
                    color={DOC_STATUS_COLORS[info.docStatus] || 'default'}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  문서번호: {info.refDocNo || '-'} / 상신일: {info.submittedAt ? dayjs(info.submittedAt).format('YYYY-MM-DD HH:mm') : '-'}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                {canRecall && (
                  <Button color="warning" variant="outlined" onClick={() => setRecallVisible(true)}>
                    회수
                  </Button>
                )}
                {canApprove && (
                  <>
                    <Button
                      color="success"
                      variant="contained"
                      onClick={() => setActionDialog({ open: true, mode: 'APPROVE' })}
                    >
                      승인
                    </Button>
                    <Button
                      color="error"
                      variant="outlined"
                      onClick={() => setActionDialog({ open: true, mode: 'REJECT' })}
                    >
                      반려
                    </Button>
                  </>
                )}
                <Button color="inherit" onClick={() => navigate(backTarget)}>
                  목록
                </Button>
              </Stack>
            </Stack>
          </Box>

          <Alert severity="info">
            현재 결재자: {info.currentActorNm || '-'} / 기안자: {info.submittedByNm || '-'}
          </Alert>

          {info.docType === 'HR_LEAVE' && leaveDetail?.info && (
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                휴가 신청 정보
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">신청자</Typography>
                  <Typography variant="body2">{leaveDetail.info.usrNm} {leaveDetail.info.dptNm ? `(${leaveDetail.info.dptNm})` : ''}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">기간</Typography>
                  <Typography variant="body2">
                    {dayjs(leaveDetail.info.leaveDtSt).format('YYYY-MM-DD')} ~ {dayjs(leaveDetail.info.leaveDtEd).format('YYYY-MM-DD')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">일수</Typography>
                  <Typography variant="body2">{leaveDetail.info.leaveDays}일</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">사유</Typography>
                  <Typography variant="body2">{leaveDetail.info.rmk || '-'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {info.docType === 'ORDER_BOOK' && orderDetail?.receiveOrderInf && (
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                수주품의서 정보
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">프로젝트</Typography>
                  <Typography variant="body2">
                    {orderDetail.receiveOrderInf.prjNo || '-'} / {orderDetail.receiveOrderInf.prjNm || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">매출처</Typography>
                  <Typography variant="body2">{orderDetail.receiveOrderInf.partnerNm || orderDetail.receiveOrderInf.partnerInc || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">계약일</Typography>
                  <Typography variant="body2">{orderDetail.receiveOrderInf.expDay || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">계약완료일</Typography>
                  <Typography variant="body2">{orderDetail.receiveOrderInf.siDt || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">매출이익</Typography>
                  <Typography variant="body2">{orderDetail.receiveOrderInf.roProfitAmt || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">최종매출이익</Typography>
                  <Typography variant="body2">{orderDetail.receiveOrderInf.roAdjProfitAmt || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">비고</Typography>
                  <Typography variant="body2">{orderDetail.receiveOrderInf.comment || '-'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <Divider />
          <ApprovalV2HistoryPanel steps={docDetail.steps} actions={docDetail.actions} />
        </Stack>
      ) : null}

      <ApprovalV2ActionDialog
        open={actionDialog.open}
        mode={actionDialog.mode}
        title={info?.docTitle}
        onClose={() => setActionDialog({ open: false, mode: null })}
        onConfirm={handleApproveReject}
      />
      <AskDialog
        visible={recallVisible}
        title="문서 회수"
        description="상신한 문서를 회수하시겠습니까?"
        confirmText="회수"
        cancelText="취소"
        showCancel
        onConfirm={handleRecall}
        onCancle={() => setRecallVisible(false)}
      />
    </>
  );
};

export default ApprovalHybridViewContainer;
